import { useCallback } from 'react'
import logger from 'logger'
import { scrollToElement, PaymentTokenError, ValidationError } from 'helpers'
import { usePaymentFlow, useRecurly } from 'modules/payment'


// token getter for credit card flow
const useCreditCardButton = () => {
  const { recurly, elements } = useRecurly()
  const { formRef, creditCardForm, submit, isProcessing } = usePaymentFlow()

  const submitCreditCard = useCallback(() => {
    const tokenGetter = async () => {
      logger.info('[Credit card] start getting token')

      // check and submit credit card form
      const { errors } = await creditCardForm.submit()
      if (errors) {
        logger.info('[Credit card] form validation failed', {
          extra: {
            errors,
          },
        })

        if (creditCardForm.fields.firstName.node) {
          scrollToElement(creditCardForm.fields.firstName.node, {
            compensation: -16,
            smooth: true,
            onlyUp: true,
          })
        }

        throw new ValidationError(errors, 'Credit card form validation failed')
      }

      // fetch token
      const { token, error } = await new Promise((resolve, reject) => {
        // if there is a network issue, recurly doesn't throw an error, but we need to stop the flow
        const timeout = setTimeout(() => {
          reject(new Error('[Credit card] token timeout'))
        }, 60000)

        recurly.token(elements, formRef.current, (error, token) => {
          clearTimeout(timeout)
          resolve({ error, token })
        })
      })

      if (error) {
        // parse recurly error
        logger.info(`[Credit card] token error`, {
          extra: {
            error,
          },
        })

        if (error.details?.length) {
          // we send errors to credit card for to display them
          const fieldMap = {
            first_name: 'firstName',
            last_name: 'lastName',
            postal_code: 'postalCode',
            number: 'cardNumber',
            month: 'cardMonth',
            year: 'cardYear',
            cvv: 'cardCVV',
          }

          const errorsByFields = error.details.reduce((result, { field, messages }) => {
            result[fieldMap[field] || field] = messages.join(', ')
            return result
          }, {})

          creditCardForm.setErrors(errorsByFields)
        }

        throw new PaymentTokenError(error, error.message)
      }

      logger.info(`[Credit card] token fetched`, {
        extra: {
          token,
        },
      })

      return {
        tokenId: token.id,
      }
    }

    return submit({ tokenGetter, paymentMethod: 'CREDIT_CARD' })
  }, [ creditCardForm, submit, recurly, elements, formRef ])

  return {
    submitCreditCard,
    isProcessing,
  }
}


export default useCreditCardButton
